import React, { useEffect, useState } from 'react'
import { useAuthStore } from '../../../../../zustand/loginStore';
import { IClientOrder, IClientOrderFilters, IPaginatedClientOrders } from 'interfaces/IClientOrders';
import { createAuthenticatedAxiosInstance, getClientOrders } from 'api/auth';
import { ClientOrderCard } from './ClientOrderCard';
import LoadingScreen from 'components/general/LoadingScreen';
import box from '../../../../../assets/box-seam.svg';
import { ClientOrderDetails } from './ClientOrderDetails';
import CustomPagination from 'components/general/CustomPagination';
import { useBreakpoints } from 'hooks/useBreakPoints';

/**
 * Main component for the user's orders display
 */
export const UserOrders = () => {
  const { userData, token } = useAuthStore();
  const { isMobile } = useBreakpoints();
  const [activeOrderTab, setActiveOrderTab] = useState<string>("Todos");
  const [selectedOrder, setSelectedOrder] = useState<IClientOrder>();
  const [clientOrders, setClientOrders] = useState<IPaginatedClientOrders>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const userName = userData?.name;
  const statusFilters = ["Todos", "Preparando", "En tránsito", "Entregado", "Cancelado"];
  const axiosInstance = createAuthenticatedAxiosInstance(token);

  /**
   * Method that fetch the initial data of the client's orders
   */
  const fetchData = async (status: string, pageSelected: number) => {
    if(!userData)
      return;
    const user_id = userData?.id.toString();
    setIsLoading(true);
    const filter: IClientOrderFilters = { user_id , status, page: pageSelected, per_page: 20 };
    const { data } = await getClientOrders(axiosInstance, filter);
    setClientOrders(data);
    setIsLoading(false);
  }

  /**
   * This use effect filters the orders according the active order tab
   * [activeOrderTab] is a string state of the last order filter tab clicked by the user
   */
  useEffect(() => {
    fetchData(activeOrderTab, 1);
  }, [activeOrderTab]);

  /**
   * This function changes the active filter
   * @param filter is the filter clicked
   */
  const handleFilter = (filter: string) => {
    setActiveOrderTab(filter);
    setPage(1);
  }

  /**
   * Function that returns an element with the detailed information of a particular order
   * @param order is the order selected 
   */
  const handleOrderDetails = (order: IClientOrder) => setSelectedOrder(order);

  /**
   * Function that shows a component according on the existence of orders
   */
  const loadedOrders = (clientOrders?: IPaginatedClientOrders): JSX.Element => {
    if (!clientOrders)
      return <></>

    if (clientOrders.orders.length > 0) {
      return (
        <>
          {clientOrders?.orders.map((order) => (
            <ClientOrderCard
              key={order.id}
              order={order}
              handleOrderDetails={handleOrderDetails}
              isMobile={isMobile}
            />
          ))}
        </>
      );
    } else {
      return (
        <div className={`d-flex flex-column align-items-center ${isMobile ? 'mobile-no-orders' : ''}`} style={{ margin: isMobile ? '30px 1rem' : '60px 4px 4px 4px' }}>
          <img 
            src={box} 
            style={{ 
              width: isMobile ? '80px' : 'auto', 
              height: isMobile ? '80px' : 'auto',
              marginBottom: isMobile ? '1rem' : '0'
            }} 
            alt="No hay pedidos"
          />
          <strong style={{ fontSize: isMobile ? '18px' : '40px', textAlign: 'center', color: '#666' }}>
            No hay pedidos para mostrar
          </strong>
        </div>
      )
    }
  }

  /**
   * Function that close the details by resetting the selected order
   */
  const onCloseDetails = () => setSelectedOrder(undefined);

  /**
   * Function that renders rather the client's order list or a detailed one
   */
  const renderContent = () => {
    if (selectedOrder) return <ClientOrderDetails order={selectedOrder} onCloseDetails={onCloseDetails} isMobile={isMobile} />;
    if (isLoading) return <LoadingScreen loadingText="Cargando pedidos..." />;
    return loadedOrders(clientOrders);
  };

  return (
    <div>
      {!isMobile && <h1 className='welcome-heading'>Hola, {userName}</h1>}
      <div className={`${isMobile ? 'mobile-filters-container' : 'd-flex gap-3 justify-content-center'}`}>
        {statusFilters.map((filter) => (
          <button 
            key={filter}
            className={`order-filter ${filter === activeOrderTab ? "active" : ""}`} 
            onClick={() => handleFilter(filter)}
          >
            {filter}
          </button>
        ))}
      </div>
      {renderContent()}
      {!!clientOrders?.total_pages && clientOrders.total_pages > 1
        && (
          <CustomPagination
            currentPage={page}
            totalPages={clientOrders?.total_pages || 0}
            totalResults={clientOrders?.total_items || 0}
            onPageChange={(selectedPage) => {
              setPage(selectedPage);
              fetchData(activeOrderTab, selectedPage);
            }}
          />
        )}
    </div>
  )
}
